import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { useSidebarStore } from '../../store/sidebarStore';
import { useAuthStore } from '../../store/authStore';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, Database, X, Copy, Upload,
  Folder, Briefcase, User, Heart, Shield, Globe, Car, Home, Scale,
  GitBranch, Lock } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';

const ICON_OPTIONS = [
  { value: 'folder', Icon: Folder },
  { value: 'briefcase', Icon: Briefcase },
  { value: 'user', Icon: User },
  { value: 'heart-pulse', Icon: Heart },
  { value: 'shield', Icon: Shield },
  { value: 'globe', Icon: Globe },
  { value: 'car', Icon: Car },
  { value: 'home', Icon: Home },
  { value: 'scale', Icon: Scale },
];

const COLOR_OPTIONS = [
  '#DC2626', '#F97316', '#F59E0B', '#16A34A',
  '#2563EB', '#7C3AED', '#EC4899', '#6366F1',
  '#0891B2', '#6B7280'
];

function IconByName({ name, className }) {
  const found = ICON_OPTIONS.find(i => i.value === name);
  if (found) return <found.Icon className={className} />;
  return <Folder className={className} />;
}

export default function RecordTypeManager() {
  const navigate = useNavigate();
  const { triggerRefresh } = useSidebarStore();
  const tenant = useAuthStore(s => s.tenant);
  const isStudio = tenant?.slug === '__template_studio__';
  const [recordTypes, setRecordTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', icon: 'folder', color: '#6366F1' });

  const handlePublish = async (rt) => {
    if (!window.confirm(`Publish "${rt.name}" to the Shop? Existing tenant installs will NOT be affected.`)) return;
    setPublishing(rt.id);
    try {
      const res = await api.post(`/admin/record-types/${rt.id}/publish-to-shop`);
      toast.success(`Published v${res.data.new_version} of ${res.data.template_name}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Publish failed');
    } finally {
      setPublishing(null);
    }
  };

  const fetchRecordTypes = async () => {
    try {
      const { data } = await adminApi.getRecordTypes();
      setRecordTypes(data.record_types);
    } catch (err) {
      toast.error('Failed to load record types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecordTypes(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', icon: 'folder', color: '#6366F1' });
    setShowModal(true);
  };

  const openEdit = (rt) => {
    setEditing(rt);
    setForm({ name: rt.name, description: rt.description || '', icon: rt.icon, color: rt.color });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editing) {
        await adminApi.updateRecordType(editing.id, form);
        toast.success('Record type updated');
      } else {
        await adminApi.createRecordType(form);
        toast.success('Record type created');
      }
      setShowModal(false);
      fetchRecordTypes();
      triggerRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
  };

  const handleDelete = async (rt) => {
    if (!window.confirm(`Delete "${rt.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteRecordType(rt.id);
      toast.success('Record type deleted');
      fetchRecordTypes();
      triggerRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleDuplicate = async (rt) => {
    const newName = window.prompt('Name for the copy:', rt.name + ' (Copy)');
    if (!newName || !newName.trim()) return;
    try {
      const res = await adminApi.duplicateRecordType(rt.id, { name: newName.trim() });
      toast.success(`Copied! ${res.data.fields_copied} fields, ${res.data.screens_copied} screens cloned.`);
      fetchRecordTypes();
      triggerRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Duplicate failed');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <AdminPageHeader title="Record Types" description="Define what types of records your firm tracks" icon={Database} iconColor="text-blue-600" iconBg="bg-blue-50">
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
          <Plus className="h-4 w-4" /> New Record Type
        </button>
      </AdminPageHeader>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : recordTypes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Database className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No record types yet</h3>
          <p className="text-sm text-gray-500 mb-4">Create your first record type to start tracking cases, contacts, or anything else.</p>
          <button onClick={openCreate}
            className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
            <Plus className="h-4 w-4" /> Create Record Type
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {recordTypes.map((rt) => (
            <div key={rt.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:border-gray-300 transition-colors">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: rt.color + '15', color: rt.color }}>
                <IconByName name={rt.icon} className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">{rt.name}</h3>
                <p className="text-xs text-gray-500">
                  {rt.field_count} fields · {rt.record_count} records
                  {rt.workflow_name && ` · Workflow: ${rt.workflow_name}`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                  <button onClick={() => navigate(`/admin/record-types/${rt.id}/screens`)}
                    className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100">
                    Manage Screens
                  </button>
                  <button onClick={() => navigate(`/admin/record-types/${rt.id}/workflow`)}
                    className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100">
                    Workflow
                  </button>
                <button onClick={() => navigate(`/admin/record-types/${rt.id}/fields`)}
                  className="px-3 py-1.5 text-xs font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100">
                  Manage Fields
                </button>
                <button onClick={() => navigate(`/admin/record-types/${rt.id}/title`)}
                  className="px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100">
                  Title Formula
                </button>
                <button onClick={() => navigate(`/admin/record-types/${rt.id}/relationships`)}
                  className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100">
                  Relationships
                </button>
                <button onClick={() => handleDuplicate(rt)}
                  className="px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100"
                  title="Duplicate record type with all fields and screens">
                  <Copy className="h-4 w-4 inline mr-1" />Copy
                </button>
                {isStudio && !rt.is_system && rt.source_template_id && (
                  <button onClick={() => handlePublish(rt)} disabled={publishing === rt.id}
                    className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 disabled:opacity-50"
                    title="Publish to Workflow Shop">
                    <Upload className="h-4 w-4 inline mr-1" />{publishing === rt.id ? 'Publishing...' : 'Publish'}
                  </button>
                )}
                <button onClick={() => openEdit(rt)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
                  <Pencil className="h-4 w-4" />
                </button>
                {!rt.is_system && <button onClick={() => handleDelete(rt)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>}
                {rt.is_system && <Lock className="h-4 w-4 text-gray-300 ml-2" title="System type" />}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold">{editing ? 'Edit' : 'New'} Record Type</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  disabled={editing?.is_system}
                  placeholder="e.g., MVA Case, Contact, Medical Provider"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${editing?.is_system ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`} />
                {editing?.is_system && <p className="text-xs text-gray-400 mt-1">System record type — name cannot be changed</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2} placeholder="Optional description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                <div className="flex gap-2 flex-wrap">
                  {ICON_OPTIONS.map(({ value, Icon }) => (
                    <button key={value} onClick={() => setForm({ ...form, icon: value })}
                      className={`p-2 rounded-lg border-2 transition-colors ${form.icon === value ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <Icon className="h-5 w-5" style={{ color: form.color }} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLOR_OPTIONS.map((color) => (
                    <button key={color} onClick={() => setForm({ ...form, color })}
                      className={`h-8 w-8 rounded-full border-2 transition-all ${form.color === color ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: color }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleSave}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
                {editing ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
